/**
 * SkyNexa Technologies — canonical capability catalogue.
 *
 * Single source of truth. Consumed by:
 *   - stack.html      (the filterable technology matrix)
 *   - contact.html    (the enquiry form's "area of interest" picker)
 *   - scripts/generate-sitemap.mjs (nothing yet, but keeps IDs stable)
 *   - tests/unit/technologies.test.js (shape + uniqueness invariants)
 *
 * Adding a capability: append to the relevant category's `items`. Adding a
 * category: append here and it appears in the stack filters and the enquiry
 * form automatically. Do not hard-code technology names anywhere else.
 */

/** @typedef {{ name: string, note?: string }} Technology */
/** @typedef {{ id: string, label: string, plainLabel: string, blurb: string, accent: 'cyan'|'emerald'|'violet'|'amber'|'rose', items: Technology[] }} Category */

/** @type {Category[]} */
export const CATEGORIES = [
  {
    id: 'programming',
    label: 'Programming & Automation',
    plainLabel: 'Building the site',
    blurb:
      'The languages we build your website and apps in. You never see these, but they are what makes the buttons work.',
    accent: 'cyan',
    items: [
      { name: 'React', note: 'SPA & Next.js app router' },
      { name: 'Flutter', note: 'Cross-platform mobile' },
      { name: 'TypeScript', note: 'Strict-mode, end-to-end types' },
      { name: 'JavaScript', note: 'ES2023+' },
      { name: 'Node.js', note: 'APIs, workers, CLIs' },
      { name: 'Python', note: 'Automation, data, AI' },
      { name: 'Bash', note: 'Glue & operational tooling' },
    ],
  },
  {
    id: 'cloud',
    label: 'Cloud Platforms',
    plainLabel: 'Where it lives',
    blurb:
      'Where your website actually lives. These are the big, reliable data centres run by Amazon, Microsoft, Google and Oracle.',
    accent: 'amber',
    items: [
      { name: 'Amazon Web Services (AWS)' },
      { name: 'Microsoft Azure' },
      { name: 'Google Cloud Platform (GCP)' },
      { name: 'Oracle Cloud Infrastructure (OCI)' },
    ],
  },
  {
    id: 'identity',
    label: 'Identity Platforms',
    plainLabel: 'Logins & accounts',
    blurb:
      "Logins and accounts. Keeps your customers' details safe and lets the right staff into the right places.",
    accent: 'violet',
    items: [
      { name: 'AWS IAM / Cognito' },
      { name: 'Microsoft Entra ID' },
      { name: 'Azure AD' },
      { name: 'Federated SSO / OIDC' },
    ],
  },
  {
    id: 'iac',
    label: 'Infrastructure as Code',
    plainLabel: 'Setting things up',
    blurb:
      "How we set your systems up in a repeatable way, so nothing is held together by someone's memory.",
    accent: 'violet',
    items: [
      { name: 'Terraform' },
      { name: 'Terragrunt' },
      { name: 'Pulumi' },
      { name: 'AWS CloudFormation' },
      { name: 'AWS CDK' },
      { name: 'AWS SDK' },
      { name: 'ARM Templates' },
      { name: 'Google Cloud Deployment Manager' },
    ],
  },
  {
    id: 'containers',
    label: 'Containers & Platform Engineering',
    plainLabel: 'Handling busy periods',
    blurb: 'How your website is packaged and run, so it copes when a lot of people visit at once.',
    accent: 'cyan',
    items: [
      { name: 'Docker' },
      { name: 'Kubernetes', note: 'EKS, AKS, GKE' },
      { name: 'Helm' },
      { name: 'Container orchestration' },
      { name: 'Multi-tenant platform operations' },
    ],
  },
  {
    id: 'cicd',
    label: 'CI/CD & Automation',
    plainLabel: 'Safe releases',
    blurb: 'The safety checks every change passes through before it reaches your live site.',
    accent: 'emerald',
    items: [
      { name: 'GitHub Actions' },
      { name: 'Jenkins' },
      { name: 'GitLab CI/CD' },
      { name: 'AWS CodePipeline' },
      { name: 'Azure DevOps' },
      { name: 'Cloud Build' },
      { name: 'Ansible' },
      { name: 'Chef' },
      { name: 'Puppet' },
    ],
  },
  {
    id: 'observability',
    label: 'Observability & Monitoring',
    plainLabel: 'Keeping watch',
    blurb:
      'How we watch your site around the clock, so we usually know something is wrong before you do.',
    accent: 'amber',
    items: [
      { name: 'Datadog' },
      { name: 'Dynatrace' },
      { name: 'Grafana' },
      { name: 'Prometheus' },
      { name: 'ELK Stack' },
      { name: 'AWS CloudWatch' },
      { name: 'Azure Monitor' },
      { name: 'Google Cloud Monitoring' },
    ],
  },
  {
    id: 'testing',
    label: 'Testing & QA Automation',
    plainLabel: 'Catching mistakes',
    blurb: 'Automated checks that catch mistakes before your customers ever see them.',
    accent: 'emerald',
    items: [
      { name: 'Cypress' },
      { name: 'Playwright' },
      { name: 'Jest' },
      { name: 'JMeter' },
      { name: 'SonarCloud' },
      { name: 'Snyk' },
      { name: 'Postman' },
      { name: 'API testing' },
      { name: 'Load testing' },
    ],
  },
  {
    id: 'secops',
    label: 'SecOps & DevSecOps',
    plainLabel: 'Security & privacy',
    blurb:
      "Keeping your site and your customers' data safe, and proving it to auditors when you need to.",
    accent: 'rose',
    items: [
      { name: 'IAM' },
      { name: 'RBAC / ABAC' },
      { name: 'Zero Trust Architecture' },
      { name: 'KMS encryption' },
      { name: 'GDPR / SOC 2 compliance' },
      { name: 'Checkov' },
      { name: 'tfsec' },
      { name: 'Policy-as-Code' },
    ],
  },
  {
    id: 'sre',
    label: 'Site Reliability Engineering',
    plainLabel: 'Staying online',
    blurb:
      'Keeping your site up. Planning for busy periods, and getting you back quickly on the rare occasion something breaks.',
    accent: 'rose',
    items: [
      { name: 'Incident management' },
      { name: 'Root cause analysis (RCA)' },
      { name: 'Reliability engineering' },
      { name: 'High availability' },
      { name: 'Disaster recovery' },
      { name: 'SLA / SLO monitoring' },
      { name: 'Capacity planning' },
    ],
  },
  {
    id: 'finops',
    label: 'FinOps & Cost Optimization',
    plainLabel: 'Controlling costs',
    blurb:
      'Making sure you are not paying for computing power you do not use. This usually saves money every month.',
    accent: 'emerald',
    items: [
      { name: 'Cloud cost governance' },
      { name: 'Reserved instances' },
      { name: 'Spot instances' },
      { name: 'Rightsizing' },
      { name: 'Autoscaling' },
      { name: 'Resource optimization' },
      { name: 'Budgeting & forecasting' },
    ],
  },
  {
    id: 'data',
    label: 'Data Platforms',
    plainLabel: 'Your business data',
    blurb: 'Where your business information is stored, and how you get useful reports out of it.',
    accent: 'cyan',
    items: [
      { name: 'Databricks', note: 'Workspaces, clusters, Unity Catalog, jobs/pipelines' },
      { name: 'Snowflake' },
      { name: 'Redshift' },
      { name: 'Firebase' },
      { name: 'PostgreSQL' },
      { name: 'MySQL' },
      { name: 'MongoDB' },
    ],
  },
  {
    id: 'itsm',
    label: 'ITSM & Collaboration',
    plainLabel: 'Tracking the work',
    blurb: 'The tools we use to track work, so you can always see what we are doing and why.',
    accent: 'violet',
    items: [
      { name: 'ServiceNow' },
      { name: 'Jira' },
      { name: 'Confluence' },
      { name: 'Change / incident management workflows' },
    ],
  },
  {
    id: 'ai',
    label: 'AI & GenAI Engineering',
    plainLabel: 'AI features',
    blurb:
      'Adding AI features to your business: chat assistants, search that understands questions, automated summaries.',
    accent: 'violet',
    items: [
      { name: 'Claude API & Claude Code' },
      { name: 'OpenAI APIs' },
      { name: 'AI agents, subagents & agent skills' },
      { name: 'Model Context Protocol (MCP)' },
      { name: 'Amazon Bedrock' },
      { name: 'Google Cloud Vertex AI' },
      { name: 'Prompt engineering' },
      { name: 'RAG', note: 'Chunking, retrieval evaluation' },
    ],
  },
];

/** Flat list of every technology name, for search and validation. */
export const ALL_TECHNOLOGIES = CATEGORIES.flatMap((category) =>
  category.items.map((item) => ({
    ...item,
    categoryId: category.id,
    categoryLabel: category.label,
  })),
);

/**
 * What people can ask for, in the words they would actually use.
 *
 * These are deliberately not service names. Someone who needs an online shop
 * searches for "online shop", not "headless commerce"; someone whose site keeps
 * falling over does not know to ask for "reliability engineering". The mapping
 * from these to what we actually build is our job, not the visitor's.
 *
 * Order matters: the most common requests come first, and the escape hatch is
 * always last so nobody is ever stuck without an option that fits.
 */
export const ENQUIRY_INTERESTS = [
  'A brand new website',
  'A redesign of the website I already have',
  'An online shop, to sell products or services',
  'A booking, enquiry or customer form',
  'My website is slow, broken or offline',
  'Help being found on Google',
  'An app for phones',
  'Someone to look after my website day to day',
  'A custom system for my business',
  'Cloud hosting, servers or IT infrastructure',
  'Help with a student or university project',
  'Teaching, training or one-to-one help',
  'Something else entirely, please just advise me',
  "Something else, I'll explain below",
];

/**
 * Maps the `?need=` slugs used by the service-page cards onto the enquiry
 * options above, so someone arriving from a specific card lands on the form
 * with the right thing already chosen and does not have to pick twice.
 *
 * Keep in sync with the cards in services.html \u2014 a unit test asserts every
 * slug linked from that page appears here, and a browser test asserts each one
 * resolves to a real option.
 */
export const PRACTICE_TO_INTEREST = {
  'new-website': 'A brand new website',
  redesign: 'A redesign of the website I already have',
  shop: 'An online shop, to sell products or services',
  booking: 'A booking, enquiry or customer form',
  fix: 'My website is slow, broken or offline',
  google: 'Help being found on Google',
  app: 'An app for phones',
  care: 'Someone to look after my website day to day',
  custom: 'A custom system for my business',
  cloud: 'Cloud hosting, servers or IT infrastructure',
  students: 'Help with a student or university project',
  teaching: 'Teaching, training or one-to-one help',
  other: 'Something else entirely, please just advise me',
};
